/* ---------------------------------------------------------------------------
 * Custom icon set. Generated from /assets/icons-raw/ Figma exports.
 *
 *   - All icons are 24×24 viewBox. The component prop `size` (default 18) sets
 *     the rendered width + height, matching the lucide-react API we replaced.
 *   - A minimum size floor (MIN_ICON_PX) is enforced inside every component,
 *     so callers passing `size={11}` from older code still get a readable
 *     icon. These SVGs have more two-tone detail than lucide's stroke icons
 *     and turn to mush below ~16px.
 *   - Main shape is `currentColor` so the icon picks up text color from its
 *     parent (e.g. var(--muted) on rest state, var(--foreground) on hover).
 *   - The orange accent on a few icons is rendered as
 *     `var(--icon-accent, #FF3C15)` so the brand colour can be re-themed
 *     site-wide by setting --icon-accent on :root, without re-exporting SVGs.
 *   - Internal SVG ids (clip-paths etc.) are namespaced per icon so multiple
 *     instances on the page can't collide.
 * ------------------------------------------------------------------------- */
import type { SVGAttributes } from 'react'

export interface IconProps extends Omit<SVGAttributes<SVGElement>, 'children'> {
  size?: number | string
  /** Skip the MIN_ICON_PX floor. Use for genuine inline-in-text glyphs
   *  (e.g. a 12px info dot in a paragraph) where 24px would overpower the
   *  surrounding type. Chips, buttons and section heads should NOT use this. */
  noFloor?: boolean
  title?: string
}

// Minimum render size for any custom icon. Tuned to where the orange accent
// detail stops disappearing into the gray silhouette. Strings (e.g. "1em")
// pass through unchanged.
const MIN_ICON_PX = 24
const DEFAULT_ICON_PX = 24
const sizePx = (s: number | string | undefined, noFloor?: boolean): number | string => {
  if (s == null) return noFloor ? 14 : DEFAULT_ICON_PX
  if (typeof s === 'number') return noFloor ? s : Math.max(s, MIN_ICON_PX)
  return s
}

export function Activity({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M12 4.9375C12 4.33984 12.3164 3.8125 12.8438 3.49609C13.3359 3.17969 14.0039 3.17969 14.5313 3.49609C15.0234 3.8125 15.375 4.33984 15.375 4.9375C15.375 5.57031 15.0234 6.09766 14.5313 6.41406C14.0039 6.73047 13.3359 6.73047 12.8438 6.41406C12.3164 6.09766 12 5.57031 12 4.9375ZM10.8047 10.2812C10.7695 10.2812 10.7344 10.3164 10.6992 10.3164L10.418 10.4219C9.85547 10.7031 9.39844 11.1953 9.1875 11.793L9.11719 12.0391C8.90625 12.6367 8.27344 12.9531 7.67579 12.7773C7.11329 12.5664 6.79688 11.9336 6.97266 11.3359L7.07813 11.0547C7.46485 9.85938 8.34375 8.875 9.50391 8.38281L9.78516 8.24219C10.5234 7.92578 11.2969 7.75 12.1055 7.75C13.6875 7.75 15.0938 8.69922 15.6914 10.1406L16.2539 11.4414L16.9922 11.8281C17.5547 12.1094 17.7656 12.7773 17.4844 13.3398C17.2031 13.9023 16.5352 14.1133 15.9727 13.832L15.0586 13.375C14.6719 13.1992 14.3906 12.8828 14.25 12.4961L13.8984 11.6875L13.2305 14.0078L14.9531 15.9062C15.1641 16.1172 15.3047 16.3633 15.375 16.6445L16.1836 19.8789C16.3242 20.4766 15.9375 21.0742 15.3398 21.2148C14.7422 21.3906 14.1445 21.0039 13.9688 20.4062L13.1953 17.3125L10.7344 14.6055C10.207 14.043 9.9961 13.2344 10.207 12.4961L10.8047 10.2812Z" fill="var(--icon-accent, #656565)"/>
<path d="M9.64453 15.0625L8.76562 17.2422L6.69141 19.3516C6.23438 19.7734 6.23438 20.5117 6.69141 20.9336C7.11328 21.3906 7.85156 21.3906 8.27344 20.9336L10.4531 18.7539C10.5938 18.6133 10.7344 18.4023 10.8047 18.1914L11.332 16.9258L9.89062 15.3789C9.82031 15.2734 9.71484 15.168 9.64453 15.0625Z" fill="currentColor"/>
    </svg>
  )
}

export function Backpack({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M10.875 4.9375C10.6992 4.9375 10.5938 5.07812 10.5938 5.21875V6.625H13.4062V5.21875C13.4062 5.07812 13.2656 4.9375 13.125 4.9375H10.875ZM8.90625 5.21875C8.90625 4.16406 9.78516 3.25 10.875 3.25H13.125C14.1797 3.25 15.0938 4.16406 15.0938 5.21875V6.625H15.375C17.8359 6.625 19.875 8.66406 19.875 11.125V19C19.875 20.2656 18.8555 21.25 17.625 21.25V17.3125V15.625C17.625 14.3945 16.6055 13.375 15.375 13.375H8.625C7.35938 13.375 6.375 14.3945 6.375 15.625V17.3125V21.25C5.10938 21.25 4.125 20.2656 4.125 19V11.125C4.125 8.66406 6.12891 6.625 8.625 6.625H8.90625V5.21875ZM9.1875 9.4375C8.87109 9.4375 8.625 9.71875 8.625 10C8.625 10.3164 8.87109 10.5625 9.1875 10.5625H14.8125C15.0938 10.5625 15.375 10.3164 15.375 10C15.375 9.71875 15.0938 9.4375 14.8125 9.4375H9.1875Z" fill="var(--icon-accent, #656565)"/>
<path d="M7.5 21.25H16.5V17.875H7.5V21.25Z" fill="currentColor"/>
<path d="M7.5 16.75H16.5V15.625C16.5 15.0273 15.9727 14.5 15.375 14.5H8.625C7.99219 14.5 7.5 15.0273 7.5 15.625V16.75Z" fill="currentColor"/>
    </svg>
  )
}

export function Database({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M17.9414 10.8086C18.6797 10.5625 19.3477 10.2109 19.875 9.82422V13.375C19.875 14.957 16.3242 16.1875 12 16.1875C7.64062 16.1875 4.125 14.957 4.125 13.375V9.82422C4.61719 10.2109 5.32031 10.5625 6.02344 10.8086C7.60547 11.3711 9.71484 11.6875 12 11.6875C14.25 11.6875 16.3594 11.3711 17.9414 10.8086ZM4.125 15.4492C4.61719 15.8359 5.32031 16.1875 6.02344 16.4336C7.60547 16.9961 9.71484 17.3125 12 17.3125C14.25 17.3125 16.3594 16.9961 17.9414 16.4336C18.6797 16.1875 19.3477 15.8359 19.875 15.4492V18.4375C19.875 20.0195 16.3242 21.25 12 21.25C7.64062 21.25 4.125 20.0195 4.125 18.4375V15.4492Z" fill="var(--icon-accent, #656565)"/>
<path d="M19.875 7.75V6.0625C19.875 4.51562 16.3242 3.25 12 3.25C7.64062 3.25 4.125 4.51562 4.125 6.0625V7.75C4.125 9.33203 7.64062 10.5625 12 10.5625C16.3242 10.5625 19.875 9.33203 19.875 7.75Z" fill="currentColor"/>
    </svg>
  )
}

export function StarFilled({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M12.9843 3.88281L15.2695 8.55859L20.2968 9.29688C20.7187 9.36719 21.0703 9.64844 21.2109 10.0703C21.3515 10.457 21.246 10.9141 20.9296 11.1953L17.2734 14.8164L18.1523 19.9492C18.2226 20.3711 18.0468 20.793 17.6953 21.0391C17.3437 21.3203 16.8867 21.3203 16.5 21.1445L12 18.7188L7.4648 21.1445C7.11324 21.3203 6.65621 21.3203 6.30464 21.0391C5.95308 20.793 5.7773 20.3711 5.84761 19.9492L6.69136 14.8164L3.03511 11.1953C2.75386 10.9141 2.64839 10.457 2.75386 10.0703C2.89449 9.64844 3.24605 9.36719 3.66793 9.29688L8.73043 8.55859L10.9804 3.88281C11.1562 3.49609 11.5429 3.25 12 3.25C12.4218 3.25 12.8086 3.49609 12.9843 3.88281Z" fill="currentColor"/>
    </svg>
  )
}

export function Image({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M14.3555 10.3867C14.2148 10.1406 13.9336 10 13.6875 10C13.4062 10 13.125 10.1406 12.9844 10.3867L9.92578 14.8867L8.97656 13.6914C8.83594 13.5156 8.58984 13.375 8.34375 13.375C8.0625 13.375 7.81641 13.5156 7.67578 13.6914L5.42578 16.5039C5.21484 16.7852 5.17969 17.1367 5.32031 17.418C5.46094 17.6992 5.74219 17.875 6.09375 17.875H9.46875H10.5938H17.9062C18.1875 17.875 18.5039 17.7344 18.6445 17.4531C18.7852 17.1719 18.75 16.8203 18.5742 16.5742L14.3555 10.3867ZM6.9375 10C7.53516 10 8.0625 9.68359 8.37891 9.15625C8.69531 8.66406 8.69531 7.99609 8.37891 7.46875C8.0625 6.97656 7.53516 6.625 6.9375 6.625C6.30469 6.625 5.77734 6.97656 5.46094 7.46875C5.14453 7.99609 5.14453 8.66406 5.46094 9.15625C5.77734 9.68359 6.30469 10 6.9375 10Z" fill="currentColor"/>
<path d="M3 6.625C3 5.39453 3.98438 4.375 5.25 4.375H18.75C19.9805 4.375 21 5.39453 21 6.625V17.875C21 19.1406 19.9805 20.125 18.75 20.125H5.25C3.98438 20.125 3 19.1406 3 17.875V6.625Z" stroke="var(--icon-accent, #656565)" strokeWidth="2"/>
    </svg>
  )
}

export function Link({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M2.85938 11.8633L6.79688 7.89062C8.80078 5.92188 12 5.92188 14.0039 7.89062C15.7617 9.64844 15.9727 12.4258 14.5312 14.4648L14.4961 14.5C14.1445 15.0273 13.4062 15.1328 12.9141 14.7812C12.4219 14.4297 12.2812 13.7266 12.668 13.1992L12.7031 13.1641C13.5117 12.0039 13.3711 10.4922 12.3867 9.50781C11.2969 8.38281 9.50391 8.38281 8.37891 9.50781L4.44141 13.4453C3.35156 14.5352 3.35156 16.3281 4.44141 17.4531C5.42578 18.4375 6.97266 18.5781 8.09766 17.7695L8.13281 17.7344C8.66016 17.3477 9.36328 17.4883 9.71484 17.9805C10.0664 18.4727 9.96094 19.1758 9.46875 19.5625L9.39844 19.5977C7.39453 21.0391 4.61719 20.793 2.85938 19.0352C0.855469 17.0664 0.855469 13.832 2.85938 11.8633Z" fill="var(--icon-accent, #656565)"/>
<path d="M21.1055 12.6719L17.168 16.6094C15.1993 18.6133 11.9649 18.6133 9.99617 16.6094C8.23835 14.8516 7.99226 12.1094 9.43367 10.0703L9.46882 10.0352C9.85554 9.50781 10.5587 9.40234 11.0509 9.7539C11.543 10.1055 11.6837 10.8086 11.2969 11.3359L11.2618 11.3711C10.4532 12.4961 10.5938 14.043 11.5782 15.0273C12.668 16.1523 14.461 16.1523 15.586 15.0273L19.5235 11.0898C20.6485 9.96484 20.6485 8.17187 19.5235 7.08203C18.5391 6.09765 16.9923 5.95703 15.8673 6.76562L15.8321 6.80078C15.3048 7.1875 14.6016 7.04687 14.2501 6.55468C13.8985 6.0625 14.004 5.35937 14.4962 4.97265L14.5665 4.9375C16.6055 3.49609 19.3477 3.74218 21.1055 5.5C23.1094 7.46875 23.1094 10.7031 21.1055 12.6719Z" fill="currentColor"/>
    </svg>
  )
}

export function Moon({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M3 17.875C1.73438 17.875 0.75 16.8906 0.75 15.625C0.75 14.3945 1.73438 13.375 3 13.375H3.03516C3.31641 12.1094 4.44141 11.125 5.8125 11.125C7.04297 11.125 8.0625 11.9336 8.44922 13.0234C8.76562 12.918 9.08203 12.8125 9.46875 12.8125C10.8398 12.8125 12 13.9727 12 15.3438C12 16.75 10.8398 17.875 9.46875 17.875H3Z" fill="var(--icon-accent, #656565)"/>
<path d="M17.5898 4.375V4.41016C17.8008 4.41016 18.0469 4.41016 18.2578 4.41016C18.5039 4.44531 18.7148 4.62109 18.75 4.86719C18.8203 5.11328 18.7148 5.35938 18.5039 5.46484C16.6055 6.51953 15.3398 8.55859 15.3398 10.8438C15.3398 14.2891 18.1172 17.0312 21.5273 17.0312C21.8789 17.0312 22.2305 17.0312 22.582 16.9609C22.8281 16.9258 23.0742 17.0312 23.1797 17.2422C23.2852 17.4883 23.25 17.7344 23.0742 17.9102C21.6328 19.2812 19.7344 20.125 17.5898 20.125C15.4453 20.125 13.5117 19.2812 12.1055 17.875C12.7383 17.2422 13.125 16.3281 13.125 15.3438C13.125 13.4453 11.6484 11.8633 9.75 11.7227C10.0312 7.60938 13.4414 4.375 17.5898 4.375Z" fill="currentColor"/>
    </svg>
  )
}

export function Users({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M5.8125 3.25C6.79688 3.25 7.71094 3.8125 8.23828 4.65625C8.73047 5.53516 8.73047 6.625 8.23828 7.46875C7.71094 8.34766 6.79688 8.875 5.8125 8.875C4.79297 8.875 3.87891 8.34766 3.35156 7.46875C2.85938 6.625 2.85938 5.53516 3.35156 4.65625C3.87891 3.8125 4.79297 3.25 5.8125 3.25ZM18.75 3.25C19.7344 3.25 20.6484 3.8125 21.1758 4.65625C21.668 5.53516 21.668 6.625 21.1758 7.46875C20.6484 8.34766 19.7344 8.875 18.75 8.875C17.7305 8.875 16.8164 8.34766 16.2891 7.46875C15.7969 6.625 15.7969 5.53516 16.2891 4.65625C16.8164 3.8125 17.7305 3.25 18.75 3.25ZM0.75 13.7617C0.75 11.6875 2.40234 10 4.47656 10H5.98828C6.55078 10 7.07812 10.1406 7.57031 10.3516C7.5 10.5977 7.5 10.8789 7.5 11.125C7.5 12.4961 8.0625 13.6914 9.01172 14.5C9.01172 14.5 9.01172 14.5 8.97656 14.5H1.48828C1.06641 14.5 0.75 14.1836 0.75 13.7617ZM14.9883 14.5H14.9531C15.9023 13.6914 16.4648 12.4961 16.4648 11.125C16.4648 10.8789 16.4648 10.5977 16.4297 10.3516C16.8867 10.1406 17.4141 10 17.9766 10H19.4883C21.5625 10 23.25 11.6875 23.25 13.7617C23.25 14.1836 22.8984 14.5 22.4766 14.5H14.9883Z" fill="var(--icon-accent, #656565)"/>
<path d="M10.3125 8.20703C9.25781 8.83984 8.625 9.92969 8.625 11.125C8.625 12.3555 9.25781 13.4453 10.3125 14.0781C11.332 14.6758 12.6328 14.6758 13.6875 14.0781C14.707 13.4453 15.375 12.3555 15.375 11.125C15.375 9.92969 14.707 8.83984 13.6875 8.20703C12.6328 7.60938 11.332 7.60938 10.3125 8.20703Z" fill="currentColor"/>
<path d="M9.92578 15.625C7.32422 15.625 5.25 17.7344 5.25 20.3359C5.25 20.8633 5.67188 21.25 6.16406 21.25H17.8008C18.3281 21.25 18.75 20.8281 18.75 20.3359C18.75 17.7344 16.6406 15.625 14.0391 15.625H9.92578Z" fill="currentColor"/>
    </svg>
  )
}

export function Search({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M20.6484 19.1016C21.1055 19.5234 21.1055 20.2617 20.6484 20.6836C20.2266 21.1406 19.4883 21.1406 19.0664 20.6836L15.5928 17.21C16.1923 16.7504 16.7298 16.215 17.1914 15.6172L20.6484 19.1016Z" fill="currentColor"/>
<path d="M10.3154 3C14.3552 3.00023 17.6299 6.27561 17.6299 10.3154C17.6297 14.3551 14.3551 17.6297 10.3154 17.6299C6.27561 17.6299 3.00023 14.3552 3 10.3154C3 6.27547 6.27547 3 10.3154 3ZM10.3154 5.25C7.51811 5.25 5.25 7.51811 5.25 10.3154C5.25023 13.1126 7.51825 15.3799 10.3154 15.3799C13.1124 15.3797 15.3797 13.1124 15.3799 10.3154C15.3799 7.51825 13.1126 5.25023 10.3154 5.25Z" fill="var(--icon-accent, #656565)"/>
    </svg>
  )
}

export function Server({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M5.25 4.375H18.75C19.9805 4.375 21 5.39453 21 6.625V8.875C21 10.1406 19.9805 11.125 18.75 11.125H5.25C3.98438 11.125 3 10.1406 3 8.875V6.625C3 5.39453 3.98438 4.375 5.25 4.375ZM15.0938 6.90625C14.6016 6.90625 14.25 7.29297 14.25 7.75C14.25 8.24219 14.6016 8.59375 15.0938 8.59375C15.5508 8.59375 15.9375 8.24219 15.9375 7.75C15.9375 7.29297 15.5508 6.90625 15.0938 6.90625ZM16.7812 7.75C16.7812 8.24219 17.1328 8.59375 17.625 8.59375C18.082 8.59375 18.4688 8.24219 18.4688 7.75C18.4688 7.29297 18.082 6.90625 17.625 6.90625C17.1328 6.90625 16.7812 7.29297 16.7812 7.75Z" fill="var(--icon-accent, #656565)"/>
<path d="M18.75 13.375C19.9805 13.375 21 14.3945 21 15.625V17.875C21 19.1406 19.9805 20.125 18.75 20.125H5.25C3.98438 20.125 3 19.1406 3 17.875V15.625C3 14.3945 3.98438 13.375 5.25 13.375H18.75ZM15.0938 15.9062C14.6016 15.9062 14.25 16.293 14.25 16.75C14.25 17.2422 14.6016 17.5938 15.0938 17.5938C15.5508 17.5938 15.9375 17.2422 15.9375 16.75C15.9375 16.293 15.5508 15.9062 15.0938 15.9062ZM17.9062 15.9062C17.4141 15.9062 17.0625 16.293 17.0625 16.75C17.0625 17.2422 17.4141 17.5938 17.9062 17.5938C18.3633 17.5938 18.75 17.2422 18.75 16.75C18.75 16.293 18.3633 15.9062 17.9062 15.9062Z" fill="currentColor"/>
    </svg>
  )
}

export function Discord({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path fill="currentColor" d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286ZM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189Zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9554 2.4189-2.1568 2.4189Z"/>
    </svg>
  )
}

export function Settings({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M11.9997 3.25C12.4917 3.25 12.9842 3.32033 13.4762 3.39062C13.7926 3.42578 14.0387 3.70706 14.109 4.02344L14.5661 6.02734C15.1285 6.23825 15.6208 6.55476 16.1129 6.90625L18.0465 6.30859C18.3629 6.20315 18.7145 6.27358 18.9254 6.51953C19.2067 6.87104 19.4528 7.25787 19.6989 7.64453L19.8747 7.92578C20.0856 8.31246 20.2614 8.69927 20.4372 9.12109H20.402C20.5426 9.4375 20.4372 9.75391 20.1911 10L18.6794 11.3711C18.7145 11.6523 18.7145 11.9688 18.7145 12.25C18.7145 12.5664 18.7145 12.8828 18.6794 13.1641L20.1911 14.5352C20.4372 14.7461 20.5426 15.0977 20.402 15.4141C20.2614 15.8359 20.0856 16.2227 19.8747 16.6094L19.6989 16.8906C19.4528 17.2773 19.2067 17.6641 18.9254 17.9805C18.7145 18.2616 18.3629 18.332 18.0465 18.2266L16.1129 17.6289C15.6208 17.9804 15.0933 18.2618 14.5661 18.5078L14.109 20.5117C14.0387 20.8281 13.7926 21.0742 13.4762 21.1445C12.9842 21.2148 12.4917 21.25 11.9645 21.25C11.4724 21.25 10.98 21.2148 10.4879 21.1445C10.1717 21.0741 9.92542 20.828 9.85514 20.5117L9.3981 18.5078C8.83573 18.2618 8.34333 17.9804 7.85123 17.6289L5.91764 18.2266C5.60131 18.3319 5.24959 18.2616 5.03873 18.0156C4.75754 17.6641 4.51134 17.2773 4.26529 16.8906L4.08951 16.6094C3.87861 16.2227 3.70277 15.8359 3.56217 15.4141C3.42167 15.0978 3.52718 14.7812 3.7731 14.5352L5.28482 13.1641C5.24967 12.8828 5.24967 12.5664 5.24967 12.25C5.24967 11.9688 5.24967 11.6523 5.28482 11.3711L3.7731 10C3.52718 9.75397 3.42167 9.43738 3.56217 9.12109C3.70277 8.69927 3.87861 8.31245 4.08951 7.92578L4.26529 7.64453C4.51134 7.25788 4.75754 6.87105 5.03873 6.51953C5.24959 6.27352 5.60131 6.20328 5.91764 6.30859L7.85123 6.90625C8.34332 6.55476 8.87086 6.23828 9.3981 6.02734L9.85514 4.02344C9.92542 3.70715 10.1717 3.46104 10.4879 3.39062C10.98 3.32033 11.4725 3.25003 11.9997 3.25ZM14.2497 8.38281C12.8435 7.57438 11.1207 7.57427 9.74967 8.38281C8.34358 9.19144 7.49967 10.6681 7.49967 12.25C7.49967 13.8671 8.34358 15.3437 9.74967 16.1523C11.1207 16.9609 12.8435 16.9608 14.2497 16.1523C15.6208 15.3438 16.4997 13.8672 16.4997 12.25C16.4997 10.668 15.6208 9.19141 14.2497 8.38281Z" fill="var(--icon-accent, #656565)"/>
<path d="M14.4258 10.8438C13.8984 10 12.9844 9.4375 12 9.4375C10.9805 9.4375 10.0664 10 9.53906 10.8438C9.04688 11.7227 9.04688 12.8125 9.53906 13.6562C10.0664 14.5352 10.9805 15.0625 12 15.0625C12.9844 15.0625 13.8984 14.5352 14.4258 13.6562C14.918 12.8125 14.918 11.7227 14.4258 10.8438Z" fill="currentColor"/>
    </svg>
  )
}

export function Sparkles({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M4.40625 7.46875L6.09375 7.96094C6.23438 8.03125 6.375 8.17188 6.375 8.3125C6.375 8.48828 6.23438 8.62891 6.09375 8.69922L4.40625 9.15625L3.91406 10.8789C3.84375 11.0195 3.70312 11.125 3.5625 11.125C3.38672 11.125 3.24609 11.0195 3.17578 10.8789L2.71875 9.15625L0.996094 8.69922C0.855469 8.62891 0.75 8.48828 0.75 8.3125C0.75 8.17188 0.855469 8.03125 0.996094 7.96094L2.71875 7.46875L3.17578 5.78125C3.24609 5.64062 3.38672 5.5 3.5625 5.5C3.70312 5.5 3.84375 5.64062 3.91406 5.78125L4.40625 7.46875ZM21.2812 17.5938L22.9688 18.0859C23.1094 18.1562 23.25 18.2969 23.25 18.4375C23.25 18.6133 23.1094 18.7539 22.9688 18.8242L21.2812 19.2812L20.7891 21.0039C20.7188 21.1445 20.5781 21.25 20.4375 21.25C20.2617 21.25 20.1211 21.1445 20.0508 21.0039L19.5938 19.2812L17.8711 18.8242C17.7305 18.7539 17.625 18.6133 17.625 18.4375C17.625 18.2969 17.7305 18.1562 17.8711 18.0859L19.5938 17.5938L20.0508 15.9062C20.1211 15.7656 20.2617 15.625 20.4375 15.625C20.5781 15.625 20.7188 15.7656 20.7891 15.9062L21.2812 17.5938Z" fill="currentColor"/>
<path d="M12 3.25C12.5977 3.25 13.125 3.77734 13.125 4.375V11.6875C13.125 12.0039 13.3711 12.25 13.6875 12.25C13.9688 12.25 14.25 12.0039 14.25 11.6875V5.5C14.25 4.90234 14.7422 4.375 15.375 4.375C15.9727 4.375 16.5 4.90234 16.5 5.5V11.6875C16.5 12.0039 16.7461 12.25 17.0625 12.25C17.3438 12.25 17.625 12.0039 17.625 11.6875V7.75C17.625 7.15234 18.1172 6.625 18.75 6.625C19.3477 6.625 19.875 7.15234 19.875 7.75V14.6406C19.4532 14.7812 19.1016 15.1328 18.9961 15.5898L18.6797 16.7148L17.5547 16.9961C16.9219 17.207 16.5 17.7695 16.5 18.4375C16.5 19.1055 16.9219 19.7031 17.5547 19.8789C16.5 20.7578 15.129 21.25 13.6875 21.25H12.9844C10.9102 21.25 8.87114 20.4766 7.35942 19L3.42192 15.2383C2.85942 14.7109 2.82426 13.832 3.38676 13.2695C3.91411 12.707 4.79301 12.6719 5.35551 13.1992L7.50005 15.2383C7.50005 15.2031 7.50005 15.1328 7.50005 15.0625V5.5C7.50005 4.90234 7.99223 4.375 8.62505 4.375C9.2227 4.375 9.75005 4.90234 9.75005 5.5V11.6875C9.75005 12.0039 9.99614 12.25 10.3125 12.25C10.5938 12.25 10.875 12.0039 10.875 11.6875V4.375C10.875 3.77734 11.3672 3.25 12 3.25ZM11.7188 14.7461C11.6836 14.6055 11.5782 14.5 11.4375 14.5C11.2969 14.5 11.1563 14.6055 11.1211 14.7461L10.875 15.625L9.96098 15.9062C9.82036 15.9414 9.75005 16.0469 9.75005 16.1875C9.75005 16.3281 9.82036 16.4688 9.96098 16.5039L10.875 16.75L11.1211 17.6641C11.1563 17.8047 11.2969 17.875 11.4375 17.875C11.5782 17.875 11.6836 17.8047 11.7188 17.6641L12 16.75L12.879 16.5039C13.0196 16.4688 13.125 16.3281 13.125 16.1875C13.125 16.0469 13.0196 15.9414 12.879 15.9062L12 15.625L11.7188 14.7461Z" fill="var(--icon-accent, #656565)"/>
    </svg>
  )
}

export function Sun({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M10.875 6.0625V4.375C10.875 3.77734 11.3672 3.25 12 3.25C12.5977 3.25 13.125 3.77734 13.125 4.375V6.0625C13.125 6.69531 12.5977 7.1875 12 7.1875C11.3672 7.1875 10.875 6.69531 10.875 6.0625ZM3 12.25C3 11.6523 3.49219 11.125 4.125 11.125H5.8125C6.41016 11.125 6.9375 11.6523 6.9375 12.25C6.9375 12.8828 6.41016 13.375 5.8125 13.375H4.125C3.49219 13.375 3 12.8828 3 12.25ZM18.1875 11.125H19.875C20.4727 11.125 21 11.6523 21 12.25C21 12.8828 20.4727 13.375 19.875 13.375H18.1875C17.5547 13.375 17.0625 12.8828 17.0625 12.25C17.0625 11.6523 17.5547 11.125 18.1875 11.125ZM12 21.25C11.3672 21.25 10.875 20.7578 10.875 20.125V18.4375C10.875 17.8398 11.3672 17.3125 12 17.3125C12.5977 17.3125 13.125 17.8398 13.125 18.4375V20.125C13.125 20.7578 12.5977 21.25 12 21.25ZM5.56641 18.6836C5.10938 18.2617 5.10938 17.5234 5.56641 17.1016L6.69141 15.9766C7.11328 15.5195 7.85156 15.5195 8.27344 15.9766C8.73047 16.3984 8.73047 17.1367 8.27344 17.5586L7.14844 18.6836C6.72656 19.1406 5.98828 19.1406 5.56641 18.6836ZM15.6914 6.97656L16.8164 5.85156C17.2383 5.39453 17.9766 5.39453 18.3984 5.85156C18.8555 6.27344 18.8555 7.01172 18.3984 7.43359L17.2734 8.55859C16.8516 9.01562 16.1133 9.01562 15.6914 8.55859C15.2344 8.13672 15.2344 7.39844 15.6914 6.97656ZM18.3984 18.6836C17.9766 19.1406 17.2383 19.1406 16.8164 18.6836L15.6914 17.5586C15.2344 17.1367 15.2344 16.3984 15.6914 15.9766C16.1133 15.5195 16.8516 15.5195 17.2734 15.9766L18.3984 17.1016C18.8555 17.5234 18.8555 18.2617 18.3984 18.6836ZM6.69141 8.55859L5.56641 7.43359C5.10938 7.01172 5.10938 6.27344 5.56641 5.85156C5.98828 5.39453 6.72656 5.39453 7.14844 5.85156L8.27344 6.97656C8.73047 7.39844 8.73047 8.13672 8.27344 8.55859C7.85156 9.01562 7.11328 9.01562 6.69141 8.55859Z" fill="currentColor"/>
<path d="M10.3125 9.33203C9.25781 9.96484 8.625 11.0547 8.625 12.25C8.625 13.4805 9.25781 14.5703 10.3125 15.2031C11.332 15.8008 12.6328 15.8008 13.6875 15.2031C14.707 14.5703 15.375 13.4805 15.375 12.25C15.375 11.0547 14.707 9.96484 13.6875 9.33203C12.6328 8.73438 11.332 8.73438 10.3125 9.33203Z" fill="var(--icon-accent, #656565)"/>
    </svg>
  )
}

export function TrendingUp({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M5.25 5.5V17.3125C5.25 17.6289 5.49609 17.875 5.8125 17.875H19.875C20.4727 17.875 21 18.4023 21 19C21 19.6328 20.4727 20.125 19.875 20.125H5.8125C4.23047 20.125 3 18.8945 3 17.3125V5.5C3 4.90234 3.49219 4.375 4.125 4.375C4.72266 4.375 5.25 4.90234 5.25 5.5Z" fill="var(--icon-accent, #656565)"/>
<path d="M15.0234 13.0586L19.5234 8.55859C19.9805 8.13672 19.9805 7.39844 19.5234 6.97656C19.1016 6.51953 18.3633 6.51953 17.9414 6.97656L14.25 10.668L12.2109 8.66406C11.7891 8.20703 11.0508 8.20703 10.6289 8.66406L6.69141 12.6016C6.23438 13.0234 6.23438 13.7617 6.69141 14.1836C7.11328 14.6406 7.85156 14.6406 8.27344 14.1836L11.4375 11.0547L13.4414 13.0586C13.8633 13.5156 14.6016 13.5156 15.0234 13.0586Z" fill="currentColor"/>
    </svg>
  )
}

export function Sprout({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M3 6.625C3 6.02734 3.49219 5.5 4.125 5.5H5.25C9.57422 5.5 13.125 9.05078 13.125 13.375V14.5V20.125C13.125 20.7578 12.5977 21.25 12 21.25C11.3672 21.25 10.875 20.7578 10.875 20.125V14.5C6.51562 14.5 3 10.9844 3 6.625Z" fill="var(--icon-accent, #656565)"/>
<path d="M14.1445 12.1797C18.0117 11.6875 21 8.38281 21 4.375C21 3.77734 20.4727 3.25 19.875 3.25H18.75C15.7969 3.25 13.1953 4.90234 11.8594 7.29297C13.0898 8.62891 13.8984 10.3164 14.1445 12.1797Z" fill="currentColor"/>
    </svg>
  )
}

export function Blog({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M8.5625 5.625C8.05469 5.625 7.625 6.05469 7.625 6.5625V18.4375C7.625 18.75 7.58594 19.1016 7.46875 19.375H19.1875C19.6953 19.375 20.125 18.9453 20.125 18.4375V6.5625C20.125 6.05469 19.6953 5.625 19.1875 5.625H8.5625ZM4.8125 21.25C3.25 21.25 2 20 2 18.4375V6.875C2 6.36719 2.42969 5.9375 2.9375 5.9375C3.44531 5.9375 3.875 6.36719 3.875 6.875V18.4375C3.875 18.9453 4.30469 19.375 4.8125 19.375C5.32031 19.375 5.75 18.9453 5.75 18.4375V6.5625C5.75 5 7 3.75 8.5625 3.75H19.1875C20.75 3.75 22 5 22 6.5625V18.4375C22 20 20.75 21.25 19.1875 21.25H4.8125Z" fill="var(--icon-accent, #656565)"/>
      <path d="M10.4375 7.5C9.92969 7.5 9.5 7.92969 9.5 8.4375V10.3125C9.5 10.8203 9.92969 11.25 10.4375 11.25H12.3125C12.8203 11.25 13.25 10.8203 13.25 10.3125V8.4375C13.25 7.92969 12.8203 7.5 12.3125 7.5H10.4375Z" fill="currentColor"/>
      <path d="M17.3125 9.375H15.4375C14.9297 9.375 14.5 9.80469 14.5 10.3125C14.5 10.8203 14.9297 11.25 15.4375 11.25H17.3125C17.8203 11.25 18.25 10.8203 18.25 10.3125C18.25 9.80469 17.8203 9.375 17.3125 9.375Z" fill="currentColor"/>
      <path d="M17.3125 12.5H10.4375C9.92969 12.5 9.5 12.9297 9.5 13.4375C9.5 13.9453 9.92969 14.375 10.4375 14.375H17.3125C17.8203 14.375 18.25 13.9453 18.25 13.4375C18.25 12.9297 17.8203 12.5 17.3125 12.5Z" fill="currentColor"/>
      <path d="M17.3125 15.625H10.4375C9.92969 15.625 9.5 16.0547 9.5 16.5625C9.5 17.0703 9.92969 17.5 10.4375 17.5H17.3125C17.8203 17.5 18.25 17.0703 18.25 16.5625C18.25 16.0547 17.8203 15.625 17.3125 15.625Z" fill="currentColor"/>
    </svg>
  )
}

export function Check({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M19.5234 6.97656C19.9805 7.39844 19.9805 8.13672 19.5234 8.55859L10.5234 17.5586C10.1016 18.0156 9.36328 18.0156 8.94141 17.5586L4.44141 13.0586C3.98438 12.6367 3.98438 11.8984 4.44141 11.4766C4.86328 11.0195 5.60156 11.0195 6.02344 11.4766L9.75 15.168L17.9414 6.97656C18.3633 6.51953 19.1016 6.51953 19.5234 6.97656Z" fill="var(--icon-accent, #656565)"/>
    </svg>
  )
}

export function ChevronDown({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M11.1914 17.5586L4.44141 10.8086C3.98438 10.3867 3.98438 9.64844 4.44141 9.22656C4.86328 8.76953 5.60156 8.76953 6.02344 9.22656L12 15.168L17.9414 9.22656C18.3633 8.76953 19.1016 8.76953 19.5234 9.22656C19.9805 9.64844 19.9805 10.3867 19.5234 10.8086L12.7734 17.5586C12.3516 18.0156 11.6133 18.0156 11.1914 17.5586Z" fill="var(--icon-accent, #656565)"/>
    </svg>
  )
}

export function ChevronLeft({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M6.69141 11.4766L13.4414 4.72656C13.8633 4.26953 14.6016 4.26953 15.0234 4.72656C15.4805 5.14844 15.4805 5.88672 15.0234 6.30859L9.08203 12.25L15.0234 18.2266C15.4805 18.6484 15.4805 19.3867 15.0234 19.8086C14.6016 20.2656 13.8633 20.2656 13.4414 19.8086L6.69141 13.0586C6.23438 12.6367 6.23438 11.8984 6.69141 11.4766Z" fill="var(--icon-accent, #656565)"/>
    </svg>
  )
}

export function ChevronRight({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <g clipPath="url(#clip0_41_1204__chevronright)">
<path d="M17.2734 11.4766C17.7305 11.8984 17.7305 12.6367 17.2734 13.0586L10.5234 19.8086C10.1016 20.2656 9.36328 20.2656 8.94141 19.8086C8.48438 19.3867 8.48438 18.6484 8.94141 18.2266L14.8828 12.25L8.94141 6.30859C8.48438 5.88672 8.48438 5.14844 8.94141 4.72656C9.36328 4.26953 10.1016 4.26953 10.5234 4.72656L17.2734 11.4766Z" fill="var(--icon-accent, #656565)"/>
</g>
<defs>
<clipPath id="clip0_41_1204__chevronright">
<rect width="24" height="24" fill="white"/>
</clipPath>
</defs>
    </svg>
  )
}

export function ChevronUp({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M11.1914 6.97656C11.6133 6.51953 12.3516 6.51953 12.7734 6.97656L19.5234 13.7266C19.9805 14.1484 19.9805 14.8867 19.5234 15.3086C19.1016 15.7656 18.3633 15.7656 17.9414 15.3086L12 9.36719L6.02344 15.3086C5.60156 15.7656 4.86328 15.7656 4.44141 15.3086C3.98438 14.8867 3.98438 14.1484 4.44141 13.7266L11.1914 6.97656Z" fill="var(--icon-accent, #656565)"/>
    </svg>
  )
}

export function Copy({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M5.8125 7.75H8.625V10H6.375V19H13.125V17.875H15.375V19.5625C15.375 20.5117 14.6016 21.25 13.6875 21.25H5.8125C4.86328 21.25 4.125 20.5117 4.125 19.5625V9.4375C4.125 8.52344 4.86328 7.75 5.8125 7.75Z" fill="currentColor"/>
<path d="M11.4375 3.25H15.7969C16.2188 3.25 16.6758 3.46094 16.9922 3.77734L19.3477 6.13281C19.6641 6.44922 19.875 6.90625 19.875 7.32812V15.0625C19.875 16.0117 19.1016 16.75 18.1875 16.75H11.4375C10.4883 16.75 9.75 16.0117 9.75 15.0625V4.9375C9.75 4.02344 10.4883 3.25 11.4375 3.25Z" fill="var(--icon-accent, #656565)"/>
    </svg>
  )
}

export function ExternalLink({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M5.8125 4.375H9.75C10.3477 4.375 10.875 4.90234 10.875 5.5C10.875 6.13281 10.3477 6.625 9.75 6.625H5.8125C5.49609 6.625 5.25 6.90625 5.25 7.1875V18.4375C5.25 18.7539 5.49609 19 5.8125 19H17.0625C17.3438 19 17.625 18.7539 17.625 18.4375V14.5C17.625 13.9023 18.1172 13.375 18.75 13.375C19.3477 13.375 19.875 13.9023 19.875 14.5V18.4375C19.875 20.0195 18.6094 21.25 17.0625 21.25H5.8125C4.23047 21.25 3 20.0195 3 18.4375V7.1875C3 5.64062 4.23047 4.375 5.8125 4.375Z" fill="var(--icon-accent, #656565)"/>
<path d="M19.875 3.25H14.25C13.6172 3.25 13.125 3.77734 13.125 4.375C13.125 5.00781 13.6172 5.5 14.25 5.5H17.1328L10.0664 12.6016C9.60938 13.0234 9.60938 13.7617 10.0664 14.1836C10.4883 14.6406 11.2266 14.6406 11.6484 14.1836L18.75 7.11719V10C18.75 10.6328 19.2422 11.125 19.875 11.125C20.4727 11.125 21 10.6328 21 10V4.375C21 3.77734 20.4727 3.25 19.875 3.25Z" fill="currentColor"/>
    </svg>
  )
}

export function Flag({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M6.375 20.125V15.625V4.375C6.375 3.77734 5.84766 3.25 5.25 3.25C4.61719 3.25 4.125 3.77734 4.125 4.375V20.125C4.125 20.7578 4.61719 21.25 5.25 21.25C5.84766 21.25 6.375 20.7578 6.375 20.125Z" fill="var(--icon-accent, #656565)"/>
<path d="M19.3477 9.26172C19.6641 9.36719 19.875 9.68359 19.875 10C19.875 10.3516 19.6641 10.668 19.3477 10.7734L8 15.0166V4.98633L19.3477 9.26172Z" fill="currentColor"/>
    </svg>
  )
}

export function Clock({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M19.3125 12.25C19.3125 9.64844 17.9062 7.25781 15.6562 5.92188C13.3711 4.62109 10.5938 4.62109 8.34375 5.92188C6.05859 7.25781 4.6875 9.64844 4.6875 12.25C4.6875 14.8867 6.05859 17.2773 8.34375 18.6133C10.5938 19.9141 13.3711 19.9141 15.6562 18.6133C17.9062 17.2773 19.3125 14.8867 19.3125 12.25ZM3 12.25C3 9.05078 4.6875 6.09766 7.5 4.48047C10.2773 2.86328 13.6875 2.86328 16.5 4.48047C19.2773 6.09766 21 9.05078 21 12.25C21 15.4844 19.2773 18.4375 16.5 20.0547C13.6875 21.6719 10.2773 21.6719 7.5 20.0547C4.6875 18.4375 3 15.4844 3 12.25Z" fill="var(--icon-accent, #656565)"/>
<path d="M12 6.625C11.5078 6.625 11.1562 7.01172 11.1562 7.46875V12.25C11.1562 12.5312 11.2969 12.8125 11.5078 12.9531L14.8828 15.2031C15.2695 15.4844 15.7969 15.3789 16.0781 14.9922C16.3242 14.6055 16.2188 14.0781 15.832 13.7969L12.8438 11.8281V7.46875C12.8438 7.01172 12.457 6.625 12 6.625Z" fill="currentColor"/>
    </svg>
  )
}

export function Info({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M12 4.9375C9.36328 4.9375 6.97266 6.34375 5.63672 8.59375C4.33594 10.8789 4.33594 13.6562 5.63672 15.9062C6.97266 18.1914 9.36328 19.5625 12 19.5625C14.6016 19.5625 16.9922 18.1914 18.3281 15.9062C19.6289 13.6562 19.6289 10.8789 18.3281 8.59375C16.9922 6.34375 14.6016 4.9375 12 4.9375ZM12 21.25C8.76562 21.25 5.8125 19.5625 4.19531 16.75C2.57812 13.9727 2.57812 10.5625 4.19531 7.75C5.8125 4.97266 8.76562 3.25 12 3.25C15.1992 3.25 18.1523 4.97266 19.7695 7.75C21.3867 10.5625 21.3867 13.9727 19.7695 16.75C18.1523 19.5625 15.1992 21.25 12 21.25Z" fill="var(--icon-accent, #656565)"/>
<path d="M11.4375 15.0625H10.5938C10.1016 15.0625 9.75 15.4492 9.75 15.9062C9.75 16.3984 10.1016 16.75 10.5938 16.75H13.4062C13.8633 16.75 14.25 16.3984 14.25 15.9062C14.25 15.4492 13.8633 15.0625 13.4062 15.0625H13.125V11.9688C13.125 11.5117 12.7383 11.125 12.2812 11.125H10.5938C10.1016 11.125 9.75 11.5117 9.75 11.9688C9.75 12.4609 10.1016 12.8125 10.5938 12.8125H11.4375V15.0625Z" fill="currentColor"/>
<path d="M10.875 8.875C10.875 9.50781 11.3672 10 12 10C12.5977 10 13.125 9.50781 13.125 8.875C13.125 8.27734 12.5977 7.75 12 7.75C11.3672 7.75 10.875 8.27734 10.875 8.875Z" fill="currentColor"/>
    </svg>
  )
}

export function Locate({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M12 3.25C12.5977 3.25 13.125 3.77734 13.125 4.375V5.60547C15.9375 6.09766 18.1523 8.3125 18.6445 11.125H19.875C20.4727 11.125 21 11.6523 21 12.25C21 12.8828 20.4727 13.375 19.875 13.375H18.6445C18.1523 16.2227 15.9375 18.4375 13.125 18.9297V20.125C13.125 20.7578 12.5977 21.25 12 21.25C11.3672 21.25 10.875 20.7578 10.875 20.125V18.9297C8.02734 18.4375 5.8125 16.2227 5.32031 13.375H4.125C3.49219 13.375 3 12.8828 3 12.25C3 11.6523 3.49219 11.125 4.125 11.125H5.32031C5.8125 8.3125 8.02734 6.09766 10.875 5.60547V4.375C10.875 3.77734 11.3672 3.25 12 3.25ZM7.5 12.25C7.5 13.8672 8.34375 15.3438 9.75 16.1523C11.1211 16.9609 12.8438 16.9609 14.25 16.1523C15.6211 15.3438 16.5 13.8672 16.5 12.25C16.5 10.668 15.6211 9.19141 14.25 8.38281C12.8438 7.57422 11.1211 7.57422 9.75 8.38281C8.34375 9.19141 7.5 10.668 7.5 12.25Z" fill="var(--icon-accent, #656565)"/>
<path d="M14.4258 10.8438C13.8984 10 12.9844 9.4375 12 9.4375C10.9805 9.4375 10.0664 10 9.53906 10.8438C9.04688 11.7227 9.04688 12.8125 9.53906 13.6562C10.0664 14.5352 10.9805 15.0625 12 15.0625C12.9844 15.0625 13.8984 14.5352 14.4258 13.6562C14.918 12.8125 14.918 11.7227 14.4258 10.8438Z" fill="currentColor"/>
    </svg>
  )
}

export function User({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M10.3828 13.9375H13.582C17.0625 13.9375 19.875 16.75 19.875 20.2305C19.875 20.793 19.3828 21.25 18.8203 21.25H5.14453C4.58203 21.25 4.125 20.793 4.125 20.2305C4.125 16.75 6.90234 13.9375 10.3828 13.9375Z" fill="var(--icon-accent, #656565)"/>
<path d="M8.09766 10C8.90625 11.4062 10.3828 12.25 12 12.25C13.582 12.25 15.0586 11.4062 15.8672 10C16.6758 8.62891 16.6758 6.90625 15.8672 5.5C15.0586 4.12891 13.582 3.25 12 3.25C10.3828 3.25 8.90625 4.12891 8.09766 5.5C7.28906 6.90625 7.28906 8.62891 8.09766 10Z" fill="currentColor"/>
    </svg>
  )
}

export function Send({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M4.96476 4.23402C3.76961 3.70694 2.53938 5.04274 3.17179 6.20277L5.63273 10.7379C5.77333 11.0191 6.05466 11.1949 6.40616 11.2653L6.44523 11.2702H16.1825C17.2643 11.2702 17.5757 9.79131 16.5857 9.35506L4.96476 4.23402Z" fill="var(--icon-accent, #656565)"/>
<path d="M4.96476 19.8816C3.76961 20.4087 2.53938 19.0729 3.17179 17.9128L5.63273 13.3777C5.77333 13.0965 6.05466 12.9207 6.40616 12.8503L6.44523 12.8454H16.1825C17.2643 12.8454 17.5757 14.3243 16.5857 14.7605L4.96476 19.8816Z" fill="currentColor"/>
    </svg>
  )
}

export function Share({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M14.9883 4.69141C14.6719 4.375 14.1797 4.30469 13.793 4.48047C13.3711 4.65625 13.125 5.07812 13.125 5.5V7.75V13.375V15.625C13.125 16.082 13.3711 16.5039 13.793 16.6797C14.1797 16.8555 14.6719 16.7852 14.9883 16.4687L20.6133 11.4062C20.8594 11.1953 21 10.9141 21 10.5625C21 10.2461 20.8594 9.96484 20.6133 9.75391L14.9883 4.69141Z" fill="var(--icon-accent, #656565)"/>
<path d="M9.75 13.375H12V7.75H9.1875C5.74219 7.75 3 10.5273 3 13.9375C3 17.9453 5.84766 19.7031 6.51562 20.0898C6.58594 20.125 6.69141 20.125 6.79688 20.125C7.18359 20.125 7.5 19.8438 7.5 19.457C7.5 19.1758 7.32422 18.9297 7.14844 18.7539C6.79688 18.4375 6.375 17.8398 6.375 16.75C6.375 14.8867 7.88672 13.375 9.75 13.375Z" fill="currentColor"/>
    </svg>
  )
}

export function Sliders({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M17.5852 5.6C19.3994 5.60008 20.8704 7.07095 20.8704 8.88515C20.8703 10.6993 19.3993 12.1702 17.5852 12.1703C15.771 12.1703 14.3001 10.6993 14.3 8.88515C14.3 7.0709 15.7709 5.6 17.5852 5.6ZM17.5852 7.38515C16.7568 7.38515 16.0852 8.05673 16.0852 8.88515C16.0853 9.71351 16.7568 10.3852 17.5852 10.3852C18.4135 10.3851 19.0851 9.71345 19.0852 8.88515C19.0852 8.05679 18.4135 7.38525 17.5852 7.38515Z" fill="currentColor"/>
<path fillRule="evenodd" clipRule="evenodd" d="M6.375 12.25C7.81641 12.25 9.08203 13.1992 9.53906 14.5H19.875C20.4727 14.5 21 15.0273 21 15.625C21 16.2578 20.4727 16.75 19.875 16.75H9.53906C9.08203 18.0859 7.81641 19 6.375 19C4.51172 19 3 17.4883 3 15.625C3 13.7617 4.51172 12.25 6.375 12.25ZM6.375 14.5C5.74219 14.5 5.25 15.0273 5.25 15.625C5.25 16.2578 5.74219 16.75 6.375 16.75C6.97266 16.75 7.5 16.2578 7.5 15.625C7.5 15.0273 6.97266 14.5 6.375 14.5Z" fill="var(--icon-accent, #656565)"/>
<path d="M13.3174 7.75C13.2213 8.1121 13.1699 8.49243 13.1699 8.88477C13.1699 9.26998 13.2188 9.64379 13.3115 10H4.125C3.49219 10 3 9.50781 3 8.875C3 8.27734 3.49219 7.75 4.125 7.75H13.3174Z" fill="currentColor"/>
    </svg>
  )
}

// Ascending meter bars — used for the Vanilla Scale filter (low → high intensity).
export function Gauge({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <rect x="3" y="14" width="3.5" height="7" rx="1.25" fill="var(--icon-accent, #656565)" />
      <rect x="8.25" y="10" width="3.5" height="11" rx="1.25" fill="currentColor" />
      <rect x="13.5" y="6.5" width="3.5" height="14.5" rx="1.25" fill="var(--icon-accent, #656565)" />
      <rect x="18.75" y="3" width="3.5" height="18" rx="1.25" fill="currentColor" />
    </svg>
  )
}

export function Sunrise({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M15.6914 11.1953C15.8672 11.2656 16.0078 11.4062 16.043 11.6172L16.7461 15.3789L20.5078 16.082C20.7188 16.1172 20.8594 16.2578 20.9297 16.4336C21 16.6094 21 16.8203 20.8945 16.9609L19.875 18.4375H16.1484C15.4805 16.8203 13.8633 15.625 12 15.625C10.1016 15.625 8.48438 16.8203 7.81641 18.4375H4.125L3.07031 16.9609C2.96484 16.8203 2.96484 16.6094 3.03516 16.4336C3.10547 16.2578 3.24609 16.1172 3.45703 16.082L7.25391 15.3789L7.92188 11.6172C7.95703 11.4062 8.09766 11.2656 8.27344 11.1953C8.44922 11.125 8.66016 11.125 8.80078 11.2305L12 13.4453L15.1641 11.2305C15.3047 11.125 15.5156 11.125 15.6914 11.1953ZM1.875 20.4062C1.875 19.9492 2.22656 19.5625 2.71875 19.5625H21.2812C21.7383 19.5625 22.125 19.9492 22.125 20.4062C22.125 20.8984 21.7383 21.25 21.2812 21.25H2.71875C2.22656 21.25 1.875 20.8984 1.875 20.4062Z" fill="var(--icon-accent, #656565)"/>
<path d="M13.125 6.94141L14.3203 8.03125C14.7773 8.45312 15.5156 8.41797 15.9023 7.96094C16.3242 7.50391 16.289 6.80078 15.832 6.37891L12.7382 3.56641C12.3164 3.17969 11.6484 3.17969 11.2265 3.56641L8.13278 6.37891C7.67575 6.80078 7.64059 7.50391 8.06246 7.96094C8.48434 8.41797 9.18746 8.45312 9.6445 8.03125L10.875 6.94141V10.5625C10.875 11.1953 11.3672 11.6875 12 11.6875C12.5976 11.6875 13.125 11.1953 13.125 10.5625V6.94141Z" fill="currentColor"/>
    </svg>
  )
}

export function Sunset({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M16.043 11.6172L16.7461 15.3789L20.5078 16.082C20.7188 16.1172 20.8594 16.2578 20.9297 16.4336C21 16.6094 21 16.8203 20.8945 16.9609L19.875 18.4375H16.1484C15.4805 16.8203 13.8633 15.625 12 15.625C10.1016 15.625 8.48438 16.8203 7.81641 18.4375H4.125L3.07031 16.9609C2.96484 16.8203 2.96484 16.6094 3.03516 16.4336C3.10547 16.2578 3.24609 16.1172 3.45703 16.082L7.25391 15.3789L7.92188 11.6172C7.95703 11.4062 8.09766 11.2656 8.27344 11.1953C8.44922 11.125 8.66016 11.125 8.80078 11.2305L12 13.4453L15.1641 11.2305C15.3047 11.125 15.5156 11.125 15.6914 11.1953C15.8672 11.2656 16.0078 11.4062 16.043 11.6172ZM1.875 20.4062C1.875 19.9492 2.22656 19.5625 2.71875 19.5625H21.2812C21.7383 19.5625 22.125 19.9492 22.125 20.4062C22.125 20.8984 21.7383 21.25 21.2812 21.25H2.71875C2.22656 21.25 1.875 20.8984 1.875 20.4062Z" fill="var(--icon-accent, #656565)"/>
<path d="M15.9023 7.01172C15.5156 6.55469 14.7773 6.51953 14.3203 6.94141L13.125 8.03125V4.375C13.125 3.77734 12.5976 3.25 12 3.25C11.3672 3.25 10.875 3.77734 10.875 4.375V8.03125L9.6445 6.94141C9.18746 6.51953 8.48434 6.55469 8.06246 7.01172C7.64059 7.46875 7.67575 8.17188 8.13278 8.59375L11.2265 11.4062C11.6484 11.793 12.3164 11.793 12.7382 11.4062L15.832 8.59375C16.289 8.17188 16.3242 7.46875 15.9023 7.01172Z" fill="currentColor"/>
    </svg>
  )
}

export function AlertTriangle({ size, noFloor, className, title, ...rest }: IconProps) {
  const s = sizePx(size, noFloor)
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <g clipPath="url(#clip0_41_1175__alerttriangle)">
<path d="M11.7188 6.23828L4.75781 17.6641C4.6875 17.7695 4.6875 17.8398 4.6875 17.9453C4.6875 18.2266 4.89844 18.4375 5.17969 18.4375H18.7852C19.0664 18.4375 19.3125 18.2266 19.3125 17.9453C19.3125 17.8398 19.2773 17.7695 19.207 17.6641L12.2461 6.23828C12.2109 6.13281 12.1055 6.0625 12 6.0625C11.8594 6.0625 11.7891 6.13281 11.7188 6.23828ZM10.2773 5.35938C10.6289 4.76172 11.2969 4.375 12 4.375C12.668 4.375 13.3359 4.76172 13.6875 5.35938L20.6484 16.7852C20.8594 17.1367 21 17.5234 21 17.9453C21 19.1406 20.0156 20.125 18.7852 20.125H5.17969C3.98438 20.125 3 19.1406 3 17.9453C3 17.5234 3.10547 17.1367 3.31641 16.7852L10.2773 5.35938ZM13.125 16.1875C13.125 16.8203 12.5977 17.3125 12 17.3125C11.3672 17.3125 10.875 16.8203 10.875 16.1875C10.875 15.5898 11.3672 15.0625 12 15.0625C12.5977 15.0625 13.125 15.5898 13.125 16.1875ZM12.8438 9.71875V13.0938C12.8438 13.5859 12.457 13.9375 12 13.9375C11.5078 13.9375 11.1562 13.5859 11.1562 13.0938V9.71875C11.1562 9.26172 11.5078 8.875 12 8.875C12.457 8.875 12.8438 9.26172 12.8438 9.71875Z" fill="currentColor"/>
</g>
<defs>
<clipPath id="clip0_41_1175__alerttriangle">
<rect width="24" height="24" fill="white"/>
</clipPath>
</defs>
    </svg>
  )
}
